import type { FunctionComponent } from 'react'
import React from 'react'
import * as playerActions from 'context/player/reducer/player.actions'
import { Duration } from 'luxon'
import { CommandButton, Slider } from '../PlayerBar.styles'
import {
  ChaptersButton,
  Container,
  LoadingTrack,
  PlaybackBar,
  PlayerButtons,
  PlayPauseButton,
  SpeedButton,
  TimeStamp
} from './PlayerControls.styles'
import { usePlayerContext, useCurrentAudio } from 'context/player'
import { useTranslation } from 'i18n'
import useA11yEnabled from 'utils/hooks/use-a11y-enabled'
import VoiceMicButton from './VoiceMicButton'

const PLAYBACK_RATES = [1, 1.25, 1.5, 1.75, 2]
const EMPTY_TIMESTAMP = '--:--'

const getNextRate = (currentRate: number): number => {
  const currentIndex = PLAYBACK_RATES.indexOf(currentRate)
  const nextIndex = (currentIndex + 1) % PLAYBACK_RATES.length
  return PLAYBACK_RATES[nextIndex]
}

const formatRate = (rate: number): string => {
  return rate === 1 ? '1x' : `${rate}x`
}

const PlayerControls: FunctionComponent = () => {
  const {
    dispatch,
    state: {
      isPlaying,
      progressBar,
      currentTrackTime,
      rate,
      audioBooks,
      loading
    }
  } = usePlayerContext()
  const audio = useCurrentAudio()
  const { t } = useTranslation()
  const { a11yEnabled } = useA11yEnabled()

  function PlayPause() {
    if (isPlaying) {
      dispatch(playerActions.pause())
    } else {
      dispatch(playerActions.play())
    }
  }

  function updateTrack(target) {
    dispatch(playerActions.updateTargetTrackPecentage(parseFloat(target.value)))
  }

  return (
    <Container>
      <PlayerButtons data-testid="player-controls">
        <ChaptersButton
          data-testid="chapter-open"
          aria-label={t('Capítulos')}
          disabled={audioBooks.length < 2}
          onClick={() => dispatch(playerActions.openChaptersList())}
        />
        <VoiceMicButton />
        <CommandButton
          data-testid="back-10"
          iconName="back-10"
          aria-label={t('Voltar 10 segundos')}
          onClick={() => dispatch(playerActions.backTrack(true))}
        />

        <PlayPauseButton
          data-testid="play-pause"
          a11yEnabled={a11yEnabled}
          onClick={PlayPause}
          aria-label={isPlaying ? t('Pausar') : t('Reproduzir')}
          iconName={isPlaying ? 'pause' : 'play'}
        />

        <CommandButton
          data-testid="forward-10"
          iconName="forward-10"
          aria-label={t('Avançar 10 segundos')}
          onClick={() => dispatch(playerActions.forwardTrack(true))}
        />

        <SpeedButton
          data-testid="playback-speed"
          aria-label={t('Velocidade {{rate}}, trocar', {
            rate: formatRate(rate)
          })}
          onClick={() => dispatch(playerActions.updateRate(getNextRate(rate)))}
        >
          {formatRate(rate)}
        </SpeedButton>
      </PlayerButtons>

      <PlaybackBar>
        {loading && (
          <LoadingTrack data-testid="loading-timer">
            {EMPTY_TIMESTAMP}
          </LoadingTrack>
        )}
        {!loading && (
          <TimeStamp data-testid="current-timer">
            {Duration.fromMillis(currentTrackTime * 1000).toFormat(
              currentTrackTime >= 3600 ? 'hh:mm:ss' : 'mm:ss'
            )}
          </TimeStamp>
        )}
        <Slider
          data-testid="progress-bar-slider"
          aria-label={t('Progresso do capítulo')}
          aria-valuetext={Duration.fromMillis(currentTrackTime * 1000).toFormat(
            currentTrackTime >= 3600 ? 'hh:mm:ss' : 'mm:ss'
          )}
          value={progressBar}
          onChange={e => {
            dispatch(
              playerActions.updateProgressBar({
                value: parseFloat(e.target.value),
                isDrag: true
              })
            )
          }}
          onMouseUp={e => updateTrack(e.target)}
          onTouchEnd={e => updateTrack(e.target)}
        />
        <TimeStamp>
          {Duration.fromMillis(audio.duration * 1000).toFormat(
            audio.duration >= 3600 ? 'hh:mm:ss' : 'mm:ss'
          )}
        </TimeStamp>
      </PlaybackBar>
    </Container>
  )
}

export default PlayerControls
